import { useState, useEffect } from 'react';
import { 
  Container, Row, Col, 
  Card, Button, Form, 
  Alert, Spinner, Badge, 
  Image, Modal
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../Dashbaord/Layout.js/Layout';
import UserService from '../../../services/user.services';
import { 
  FaArrowLeft, FaEdit, FaSave, FaTimes, FaTrash,
  FaUser, FaIdCard, FaPhone, FaMapMarkerAlt,
  FaBriefcase, FaBuilding, FaCalendarAlt, FaMoneyBill,
  FaUserTag, FaUserShield, FaUserCheck, FaUserTimes,
  FaCrown
} from 'react-icons/fa';
import { colors } from '../../../utils/Colors';

const ViewAndUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResignModal, setShowResignModal] = useState(false);
  
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await UserService.getUserById(id);
      setUser(response.data);
      setFormData(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load user details. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
               type === 'number' ? parseFloat(value) || 0 : 
               value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      picture: { url: '', public_id: '' }
    }));
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      setError('');
      setSuccess('');
      
      const updatedUser = await UserService.updateUser(id, formData, profilePicture);
      
      setUser(updatedUser.data);
      setFormData(updatedUser.data);
      setIsEditing(false);
      setProfilePicture(null);
      setPreviewUrl('');
      setSuccess('User updated successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update user. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await UserService.deleteUser(id);
      setShowDeleteModal(false);
      navigate('/admin/dashboard/all-user');
    } catch (err) {
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleResign = async () => {
    try {
      const resignationData = {
        resigned: true,
        isActive: false
      };
      
      await UserService.updateUser(id, resignationData);
      setShowResignModal(false);
      fetchUserDetails();
      setSuccess('User marked as resigned successfully!');
    } catch (err) {
      setError(err.message || 'Failed to mark user as resigned.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('92')) {
      return `${cleaned.substring(0, 4)}-${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
    }
    return phone;
  };

  const getStatusBadge = () => {
    if (user?.resigned) {
      return (
        <Badge bg="danger" className="px-3 py-2 d-inline-flex align-items-center gap-2">
          <FaUserTimes />
          Resigned
        </Badge>
      );
    }
    return user?.isActive ? (
      <Badge bg="success" className="px-3 py-2 d-inline-flex align-items-center gap-2">
        <FaUserCheck />
        Active
      </Badge>
    ) : (
      <Badge bg="warning" className="px-3 py-2 d-inline-flex align-items-center gap-2">
        <FaUserTimes />
        Inactive
      </Badge>
    );
  };

  const getRoleBadge = () => {
    const roleColors = {
      'admin': { bg: 'dark', icon: <FaCrown />, label: 'ADMIN' },
      'employee': { bg: 'primary', icon: <FaUser />, label: 'EMPLOYEE' }
    };
    
    const roleConfig = roleColors[user?.role] || { bg: 'secondary', icon: <FaUser />, label: user?.role?.toUpperCase() };
    
    return (
      <Badge bg={roleConfig.bg} className="px-3 py-2 d-inline-flex align-items-center gap-2">
        {roleConfig.icon}
        {roleConfig.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading user details...</p>
        </Container>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Container className="py-5 text-center">
          <FaUserTimes className="text-muted mb-3" size={48} />
          <h3 className="text-muted">User not found</h3>
          <p className="text-muted mb-4">The user you're looking for doesn't exist.</p>
          <Button variant="primary" onClick={() => navigate('/admin/dashboard/all-user')}>
            <FaArrowLeft className="me-2" />
            Back to Users
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <Card className="border-0 shadow-sm mb-4" style={{ 
          background: colors.TABLE_HEADERS
        }}>
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="d-flex align-items-center">
                  <div>
                    <h2 className="fw-bold mb-1 text-white">Employee Details</h2>
                    <p className="text-white text-opacity-75 mb-0">
                      View and manage employee information
                    </p>
                  </div>
                </div>
              </Col>
              
              <Col md={6} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex gap-2 justify-content-md-end">
                  {!isEditing && (
                    <>
                      <Button
                        variant="outline-light"
                        onClick={() => setShowResignModal(true)}
                        disabled={user.resigned}
                      >
                        <FaUserTimes className="me-2" />
                        Mark Resigned
                      </Button>
                      <Button
                        variant="outline-light"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <FaTrash className="me-2" />
                        Delete
                      </Button>
                      <Button
                        variant="light"
                        onClick={() => setIsEditing(true)}
                      >
                        <FaEdit className="me-2" />
                        Edit Profile
                      </Button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <Button
                        variant="outline-light"
                        onClick={() => {
                          setIsEditing(false);
                          setFormData(user);
                          setProfilePicture(null);
                          setPreviewUrl('');
                        }}
                      >
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                      <Button
                        variant="light"
                        onClick={handleUpdate}
                        disabled={updating}
                      >
                        {updating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="border-0 shadow-sm mb-4">
            <FaTimes className="me-2" />
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="border-0 shadow-sm mb-4">
            <FaSave className="me-2" />
            {success}
          </Alert>
        )}

        <Row>
          <Col lg={4}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="text-center p-4">
                <div className="mb-4 position-relative">
                  {isEditing ? (
                    <>
                      <div className="position-relative d-inline-block">
                        <Image
                          src={previewUrl || formData.picture?.url || 'https://via.placeholder.com/150'}
                          roundedCircle
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                          className="border"
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle"
                          onClick={removeProfilePicture}
                          style={{ width: '30px', height: '30px' }}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                      <Form.Group className="mt-3">
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="border-dashed"
                        />
                      </Form.Group>
                    </>
                  ) : (
                    <Image
                      src={user.picture?.url || 'https://via.placeholder.com/150'}
                      roundedCircle
                      style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                      className="border"
                    />
                  )}
                </div>
                
                <h4 className="fw-bold mb-1">{user.name}</h4>
                <p className="text-muted mb-3">{user.categories || 'Employee'}</p>
                
                <div className="d-flex justify-content-center gap-2 mb-3">
                  {getStatusBadge()}
                  {getRoleBadge()}
                </div>
                
                <div className="text-start">
                  <div className="d-flex align-items-center mb-2">
                    <FaIdCard className="text-muted me-2" />
                    <span>{user.cnic}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaPhone className="text-muted me-2" />
                    <span>{formatPhone(user.phoneNumber)}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendarAlt className="text-muted me-2" />
                    <span>Joined: {formatDate(user.joinedDate)}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <FaUserTag className="text-muted me-2" />
                    <span>{user.categories}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <h6 className="mb-0 fw-bold">
                  <FaMoneyBill className="me-2" />
                  Salary Summary
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="text-center mb-3">
                  <div className="display-5 fw-bold text-primary">
                    PKR {(user.salary || 0).toLocaleString()}
                  </div>
                  <div className="text-muted small">Monthly Salary</div>
                </div>
                
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Salary:</span>
                  <span className="fw-bold">PKR {(user.salary || 0).toLocaleString()}</span>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Employment Type:</span>
                  <span className="fw-bold">{user.employmentType || 'Full-time'}</span>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0 fw-bold">
                  <FaUser className="me-2" />
                  Basic Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaUser className="me-2" />
                          Full Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaIdCard className="me-2" />
                          CNIC
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="cnic"
                          value={formData.cnic || ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaPhone className="me-2" />
                          Phone Number
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber || ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaUserTag className="me-2" />
                          Category
                        </Form.Label>
                        <Form.Select
                          name="categories"
                          value={formData.categories || ''}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        >
                          <option value="Manager">Manager</option>
                          <option value="Sweeper">Sweeper</option>
                          <option value="Waiter">Waiter</option>
                          <option value="Security">Security</option>
                          <option value="Others">Others</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaMapMarkerAlt className="me-2" />
                          Address
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="address"
                          value={formData.address || ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0 fw-bold">
                  <FaBriefcase className="me-2" />
                  Employment Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaBuilding className="me-2" />
                          Department
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="department"
                          value={formData.department || ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaCalendarAlt className="me-2" />
                          Employment Type
                        </Form.Label>
                        <Form.Select
                          name="employmentType"
                          value={formData.employmentType || ''}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        >
                          <option value="full-time">Full Time</option>
                          <option value="part-time">Part Time</option>
                          <option value="contract">Contract</option>
                          <option value="temporary">Temporary</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaCalendarAlt className="me-2" />
                          Joined Date
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="joinedDate"
                          value={formData.joinedDate ? formData.joinedDate.split('T')[0] : ''}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaUserShield className="me-2" />
                          Role
                        </Form.Label>
                        <Form.Select
                          name="role"
                          value={formData.role || ''}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        >
                          <option value="employee">Employee</option>
                          <option value="admin">Admin</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <Form.Group className="d-flex align-items-center h-100">
                        <Form.Check
                          type="switch"
                          id="isActive"
                          name="isActive"
                          label={
                            <span className="fw-bold">
                              <FaUserCheck className="me-2" />
                              Active Status
                            </span>
                          }
                          checked={formData.isActive || false}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0 fw-bold">
                  <FaMoneyBill className="me-2" />
                  Salary Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaMoneyBill className="me-2" />
                          Salary (PKR)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="salary"
                          value={formData.salary || 0}
                          onChange={handleInputChange}
                          readOnly={!isEditing}
                          className={!isEditing ? 'bg-light' : ''}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{user.name}</strong>?</p>
          <p className="text-danger">
            <FaTrash className="me-2" />
            This action cannot be undone. All user data will be permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <FaTrash className="me-2" />
            Delete Employee
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showResignModal} onHide={() => setShowResignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Mark as Resigned</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to mark <strong>{user.name}</strong> as resigned?</p>
          <p className="text-warning">
            <FaUserTimes className="me-2" />
            This will deactivate the user account and mark them as resigned.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResignModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleResign}>
            <FaUserTimes className="me-2" />
            Mark as Resigned
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default ViewAndUpdate;